import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ListUserQuery } from '@libs/user/config';
import { UsersUserMeResponse as User, UsersPaginatedUserResponse as UserResponse, UsersUserVariableSalary } from 'libs/shared/generated/src/lib/types/users';
import { firstValueFrom, Observable, of, shareReplay, tap } from 'rxjs';
import { PaginationQuery } from '../../../../../libs/shared/nest-utils/src';
import { EnvironmentService } from '../@core/services/environment.service';


@Injectable({
  providedIn: 'root'
})
export class UsersService {

  private readonly apiUrl: string = '';
  private userMe$?: Observable<User>;
  private userIsHired$?: Observable<boolean>;

  constructor(
    private readonly http: HttpClient,
    private readonly environmentService: EnvironmentService
  ) {
    // Use environment service to get the API URL
    this.apiUrl = this.environmentService.usersServiceUrl;
  }

  invalidateUsersMeCache(): void {
    this.userMe$ = undefined;
    this.userIsHired$ = undefined;
  }

  /**
   * Get all users with pagination and filtering
   */
  getAll(pagination: PaginationQuery, query: Partial<ListUserQuery> = {}): Observable<UserResponse> {
    let params = new HttpParams()
      .set('page', pagination.page.toString())
      .set('size', pagination.size.toString())
      .set('getTotalCount', pagination.getTotalCount.toString());

    // Add search parameter if present
    if (query.search) {
      params = params.set('search', query.search);
    }

    // Server-side filtering and pagination
    return this.http.get<UserResponse>(this.apiUrl, { params });
  }

  /**
   * Get logged-in user details
   */
  getUsersMe(forceRefresh: boolean = false): Observable<User> {
    if (!this.userMe$ || forceRefresh) {
      this.userMe$ = this.http
        .get<User>(`${this.apiUrl}/me`)
        .pipe(shareReplay(1));
    }

    return this.userMe$;
  }

  getPresignedUrlAvatar(): Observable<any> {
    return this.http.put<any>(`${this.apiUrl}/me/profile-picture/presigned-url`, {});
  }

  getUserAvatarThumbnail(): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}/me/profile-picture`);
  }

  uploadUserAvatar(file: File, key: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http
      .put<any>(`${this.apiUrl}/me/profile-picture/confirm-upload`, { fileKey: key })
      .pipe(tap(() => this.invalidateUsersMeCache()));
  }


  /**
   * Get a single user by ID
   */
  getById(id: string): Observable<User> {
    return this.http.get<User>(`${this.apiUrl}/${id}`);
  }

  /**
   * Create a new user
   */
  create(user: Partial<User>): Observable<User> {
    return this.http.post<User>(this.apiUrl, user);
  }

  /**
   * Update an existing user
   */
  update(id: string, user: Partial<User>): Observable<User> {
    return this.http
      .put<User>(`${this.apiUrl}/${id}`, user)
      .pipe(tap(() => this.invalidateUsersMeCache()));
  }

  /**
   * Delete a user
   */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  getUserQrCode(): Observable<any> {
    return this.http.get<{ qrCodeDataUrl: string }>(`${this.apiUrl}/me/qr-code`);
  }

  /**
   * Validate user data
   * @param user User data to validate
   * @returns Array of error messages, empty array if valid
   */
  validateUser(user: Partial<User>): string[] {
    const errors: string[] = [];

    if (!user) {
      return ['User data is missing'];
    }

    // Check required fields
    if (!user.email) {
      errors.push('Email is required');
    }

    if (!user.firstName) {
      errors.push('First name is required');
    }

    if (!user.lastName) {
      errors.push('Last name is required');
    }

    // Validate email
    if (user.email && !this.validateEmail(user.email)) {
      errors.push('Email format is invalid');
    }

    return errors;
  }

  /**
   * Validate email format
   * @param email Email to validate
   * @returns True if email format is valid, otherwise false
   */
  validateEmail(email: string): boolean {
    if (!email) return false;

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  }

  validateToken(token: string): Promise<boolean> {
    return new Promise((resolve) => {
      fetch(this.apiUrl + '/ping', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': 'it',
          'Authorization': `Bearer ${token}`,
        }
      })
        .then(response => {
          if (response.ok) {
            resolve(true);
          } else {
            resolve(false);
          }
        })
        .catch(() => {
          resolve(false);
        });
    });
  }


  getUserVariableSalary(): Observable<UsersUserVariableSalary> {
    return this.http.get<UsersUserVariableSalary>(`${this.apiUrl}/me/variable-salary`);
  }

  checkIfUserIsHired(): Observable<{ isUserHired: boolean }> {
    return this.http.get<{ isUserHired: boolean }>(`${this.apiUrl}/me/hiring-status`);
  }

  async getUserIsHired(forceRefresh: boolean = false): Promise<boolean> {
    if (!this.userIsHired$ || forceRefresh) {
      const checkIfUserHired = await firstValueFrom(this.checkIfUserIsHired());
      this.userIsHired$ = of(checkIfUserHired.isUserHired).pipe(shareReplay(1));
    }

    return firstValueFrom(this.userIsHired$);
  }
}
